import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { AddressView, Town } from '@capsico/contracts';
import { fetchLiveTowns } from '../api/discover';
import { addressLine, addressTitle } from './useAddresses';
import { setDeliveryPoint } from './useDeliveryPoint';

type GpsState = 'idle' | 'locating' | 'denied' | 'unavailable';

/**
 * Choosing where to deliver: phone GPS, a saved address, or a live town's centre. The choice is
 * stored on the device (useDeliveryPoint) and every feed / search reads it.
 */
export function useLocationPicker(options: {
  labels: {
    current: string;
    townCentre: (town: string) => string;
    home: string;
    work: string;
    other: string;
  };
  onChosen: () => void;
}) {
  const [gps, setGps] = useState<GpsState>('idle');
  const towns = useQuery({
    queryKey: ['towns', 'live'],
    queryFn: fetchLiveTowns,
    staleTime: 30 * 60_000,
  });

  const useCurrent = () => {
    if (!('geolocation' in navigator)) {
      setGps('unavailable');
      return;
    }
    setGps('locating');
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setGps('idle');
        setDeliveryPoint({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          label: options.labels.current,
          line: null,
          source: 'gps',
          addressId: null,
        });
        options.onChosen();
      },
      (failure) => setGps(failure.code === failure.PERMISSION_DENIED ? 'denied' : 'unavailable'),
      { enableHighAccuracy: true, timeout: 12_000, maximumAge: 60_000 },
    );
  };

  const chooseAddress = (address: AddressView) => {
    setDeliveryPoint({
      lat: address.lat,
      lng: address.lng,
      label: addressTitle(address, options.labels),
      line: addressLine(address),
      source: 'address',
      addressId: address.id,
    });
    options.onChosen();
  };

  const chooseTown = (town: Town) => {
    if (!town.centroid) return;
    const [lng, lat] = town.centroid;
    setDeliveryPoint({
      lat,
      lng,
      label: options.labels.townCentre(town.name),
      line: null,
      source: 'town',
      addressId: null,
    });
    options.onChosen();
  };

  return {
    gps,
    useCurrent,
    chooseAddress,
    chooseTown,
    towns: (towns.data ?? []).filter((town) => town.centroid !== null),
    townsLoading: towns.isLoading,
  };
}
